/**
 * oct20a S1 — the Stop dialog's "what did your session find?" for a Stop that ends THIS technician's
 * diagnostic session but not the diagnosis (a colleague still works it, or the repair continues on its
 * next day). CORE-I vocabulary + confidence, optional findings / open question. It is recorded as the
 * session's conclusion — never as the diagnosis result, which stays open for whoever finishes it.
 * Phone-sized controls (≥44px) below md.
 */
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CONFIDENCE_LABELS, DIAGNOSTIC_COMPETENCY_LABELS, type DiagnosticConfidence, type DiagnosticOutcome,
} from '@/types/diagnostic';
import {
  EMPTY_SESSION_CONCLUSION, SESSION_CONCLUSION_EXPLAINER, type SessionConclusionDraft,
} from '@/lib/diagnostics/evidence';
import { PHONE_TAP } from './DiagnosticEvidence';

export interface SessionConclusionFieldsProps {
  jobs: ReadonlyArray<{ jobId: string; name: string }>;
  values: Readonly<Record<string, SessionConclusionDraft>>;
  onChange: (jobId: string, next: SessionConclusionDraft) => void;
  busy: boolean;
}

export function SessionConclusionFields({ jobs, values, onChange, busy }: SessionConclusionFieldsProps) {
  if (jobs.length === 0) return null;
  return (
    <div className="space-y-3 border-t pt-3" data-testid="stop-session-conclusions">
      <p className="text-sm font-medium">What did your session find?</p>
      <p className="text-xs text-muted-foreground">{SESSION_CONCLUSION_EXPLAINER}</p>
      {jobs.map((j) => {
        const v = values[j.jobId] ?? EMPTY_SESSION_CONCLUSION;
        const set = (patch: Partial<SessionConclusionDraft>) => onChange(j.jobId, { ...v, ...patch });
        return (
          <div key={j.jobId} className="space-y-2 rounded-md border p-2" data-testid={`stop-conclusion-${j.jobId}`}>
            <p className="text-sm font-medium">{j.name}</p>
            <div className="grid gap-1" role="radiogroup" aria-label={`What your session found on ${j.name}`}>
              {(Object.keys(DIAGNOSTIC_COMPETENCY_LABELS) as DiagnosticOutcome[]).map((d) => (
                <button key={d} type="button" role="radio" aria-checked={v.disposition === d} disabled={busy}
                        data-testid={`stop-conclusion-${j.jobId}-${d}`} onClick={() => set({ disposition: d })}
                        className={cn(PHONE_TAP, 'rounded-md border px-3 py-1.5 text-left text-sm',
                          v.disposition === d ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                  {DIAGNOSTIC_COMPETENCY_LABELS[d]}
                </button>
              ))}
            </div>
            <div className="space-y-1">
              <Label>How sure are you?</Label>
              <div className="flex gap-1" role="radiogroup" aria-label={`Confidence for ${j.name}`}>
                {([1, 2, 3, 4, 5] as DiagnosticConfidence[]).map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={v.confidence === c} disabled={busy}
                          title={CONFIDENCE_LABELS[c]} data-testid={`stop-conclusion-${j.jobId}-confidence-${c}`}
                          onClick={() => set({ confidence: c })}
                          className={cn(PHONE_TAP, 'min-w-11 flex-1 rounded-md border text-sm',
                            v.confidence === c ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <Textarea aria-label={`What you found on ${j.name} (optional)`} placeholder="What you found (optional)" rows={2}
                      className="min-h-[88px]" disabled={busy} value={v.findings ?? ''}
                      onChange={(e) => set({ findings: e.target.value })} />
            <Textarea aria-label={`Still unresolved on ${j.name} (optional)`} placeholder="Still unresolved (optional)" rows={2}
                      className="min-h-[88px]" disabled={busy} value={v.unresolvedQuestions ?? ''}
                      onChange={(e) => set({ unresolvedQuestions: e.target.value })} />
          </div>
        );
      })}
    </div>
  );
}
